import { FC, Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useNsTHelper } from 'src/utils/i18n';
import {
  PlusSubscriptionIsFreeTrialAvailableSelector,
  PlusSubscriptionPlanSelectionSelector,
} from 'src/store/plusSubscription/selectors';
import { UiButton } from 'src/components/UI/Button';
import { UiOuterLink } from 'src/components/UI/OuterLink';
import { PlusSubscriptionChevronIcon } from 'src/components/plusSubscription/modals/icons/Chevron';
import { PlusSubscriptionButtonsFreeTrial } from 'src/components/plusSubscription/modals/components/Modal/Buttons/FreeTrial';
import { PlusSubscriptionSliceStateType } from 'src/store/plusSubscription/slice';
import { BaseSeparateModal } from 'src/models/modals';

import styles from 'src/styles/common/plusSubscription/modal/buttons.module.scss';

type ButtonsType = ('contactUs' | 'freeTrial' | 'noThanks')[];

type Props = BaseSeparateModal & {
  type: Extract<PlusSubscriptionSliceStateType['modal']['type'], 'planSelection' | 'trialExpired' | 'upgradeNow'>;
  onContactUsClick: () => void;
};

export const PlusSubscriptionButtons: FC<Props> = ({ type, onContactUsClick, onClose }) => {
  const tHelper = useNsTHelper('plusSubscription', 'modals.common.buttons');

  const { ComparePricingLink } = useSelector(PlusSubscriptionPlanSelectionSelector);

  const showTryForFreeButton = useSelector(PlusSubscriptionIsFreeTrialAvailableSelector);

  const buttons: ButtonsType = useMemo(() => {
    switch (type) {
      case 'upgradeNow':
        return ['contactUs'];
      case 'planSelection':
        return showTryForFreeButton ? ['freeTrial', 'contactUs'] : ['contactUs'];
      case 'trialExpired':
        return ['contactUs', 'noThanks'];
      default:
        return [];
    }
  }, [showTryForFreeButton, type]);

  return (
    <div className={`${styles.buttonsWrapper} ${type === 'trialExpired' ? styles.trialExpired : ''}`}>
      <div className={styles.buttons}>
        {buttons.map((item, index) => (
          <Fragment key={item}>
            {item === 'contactUs' && (
              <UiButton onClick={onContactUsClick} className={index ? styles.buttonBordered : styles.button}>
                {tHelper('contactUs')}
              </UiButton>
            )}

            {item === 'freeTrial' && <PlusSubscriptionButtonsFreeTrial />}

            {item === 'noThanks' && (
              <UiButton onClick={onClose} className={styles.buttonBordered}>
                {tHelper('noThanks')}
              </UiButton>
            )}
          </Fragment>
        ))}
      </div>

      <UiOuterLink className={styles.linkButton} href={ComparePricingLink}>
        {tHelper('compare')}
        <PlusSubscriptionChevronIcon className={styles.icon} />
      </UiOuterLink>
    </div>
  );
};
